import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import axiosInstance from 'Services/APIService';
import { Country, Department, LoginSignupState, User } from 'types/loginSignUpTypes';

// Add Group interface (you may need to add this to your types file as well)
export interface Group {
  group_id: string;
  label: string;
  description: string;
  avatar: string;
  peopleCount: number;
}

// Initial state
const initialState: LoginSignupState = {
  user: null,
  token: null,
  isAuthenticated: false,
  roles: [],
  departments: [],
  countries: [],
  states: [],
  cities: [],
  groups: [], // Add groups array
  selectedGroup: null, // Add selected group
  selectedRole: null, // Add selected role
  loading: false,
  error: null,
  groupsLoading: false, 

  selectedCountry: '',
  selectedState: '',
  toast: null,
  messages: []
};

// ** Login (Initiate OTP with Email and Password) **
export const login = createAsyncThunk(
  'loginSignup/login',
  async (
    { username, password, sso }: { username: string; password: string; sso: boolean },
    { rejectWithValue }
  ) => {
    try {
      // Validate inputs
      if (!username || !password) {
        return rejectWithValue('Email and password are required');
      }
      if (password.length < 5) {
        return rejectWithValue('Invalid email or password');
      }
      
      const response = await axiosInstance.post('/login', { username, password, sso });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string; error: string }>;
      
      // Handle specific error responses
      if (axiosError.response?.status === 401) {
        return rejectWithValue('Invalid email or password');
      }
      
      const errorMessage = 
        axiosError.response?.data?.message || 
        axiosError.response?.data?.error || 
        'Login failed';
      
      return rejectWithValue(errorMessage);
    }
  }
);

// ** Verify OTP for Login **
export const verifyOtp = createAsyncThunk(
  'loginSignup/verifyOtp',
  async ({ username, otp }: { username: string; otp: string }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post('/verify-otp', { username, otp });

      // After successful login, restore project state from localStorage if it exists
      setTimeout(() => {
        try {
          const savedProject = localStorage.getItem('selectedProject');
          if (savedProject) {
            const { projectId, projectName, isAdmin } = JSON.parse(savedProject);
            dispatch({
              type: 'homeTilesServices/setSelectedProject',
              payload: {
                project: projectName,
                projectId,
                isAdmin: isAdmin || false
              }
            });
          }
        } catch (error) {
          console.error('Error restoring project state:', error);
        }
      }, 100);

      return response.data; // Expects { user: {...}, token: string, groups: string[] }
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      return rejectWithValue(axiosError.response?.data?.message || 'OTP verification failed');
    }
  }
);

// ** Check if user exists **
export const checkUserExists = createAsyncThunk(
  'loginSignup/checkUserExists',
  async ({ username }: { username: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/CreateMembership/sign-up-check-user', {
        username
      });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      return rejectWithValue(axiosError.response?.data?.error || 'Email is already registered');
    }
  }
);

// ** Fetch roles and departments **
export const fetchRolesAndDepartments = createAsyncThunk(
  'loginSignup/fetchRolesAndDepartments',
  async () => {
    const response = await axiosInstance.get<{
      roles: string[];
      groups: Record<string, Department>;
    }>('/groups/list');
    return {
      roles: response.data.roles.map((item, index) => ({ name: item, id: index.toString() })),
      departments: Object.values(response.data.groups).map((item: any) => ({
        name: item['group name'],
        ...item
      }))
    };
  }
);

// ** Fetch countries and set default as "Australia" **
export const fetchCountries = createAsyncThunk(
  'loginSignup/fetchCountries',
  async (_, { dispatch }) => {
    try {
      const response = await axios.get<{ data: Country[] }>(
        'https://countriesnow.space/api/v0.1/countries/states'
      );
      const countries = response.data.data || [];
      const defaultCountry = countries.find((c) => c.name === 'Australia');
      if (defaultCountry) {
        dispatch(setSelectedCountry('Australia'));
      }
      return countries;
    } catch (error) {
      throw new Error('Failed to fetch countries');
    }
  }
);

// ** Fetch cities for selected state **
export const fetchCities = createAsyncThunk(
  'loginSignup/fetchCities',
  async ({ country, state }: { country: string; state: string }) => {
    try {
      const response = await axios.post<{ data: string[] }>(
        'https://countriesnow.space/api/v0.1/countries/state/cities',
        { country, state }
      );
      return response.data.data.map((city) => ({ name: city }));
    } catch (error) {
      throw new Error('Failed to fetch cities');
    }
  }
);

// ** Fetch Groups List - NEW **
export const fetchGroupsList = createAsyncThunk(
  'loginSignup/fetchGroupsList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/public/get-groups-list');
      return response.data.groups; // Return just the groups array
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      return rejectWithValue(axiosError.response?.data?.message || 'Failed to fetch groups list');
    }
  }
);

// ** User Signup - Updated to include group and role **
export const signupUser = createAsyncThunk(
  'loginSignup/signupUser',
  async (userData: any, { rejectWithValue }) => {
    try {
      // Map frontend fields to backend expected fields
      const payload: {
        name: string;
        confirmPassword: string;
        email: string;
        phone: string;
        dob: string;
        country_res: string;
        state: string;
        surburb: string;
        postcode: string;
        role: string[];
        groups: string[];
        department: string;
        password: string;
      } = {
        name: userData.name || userData.fullName || '',
        confirmPassword: userData.confirmPassword || '',
        email: userData.email || '',
        phone: userData.phone || '',
        dob: userData.dob || '',
        country_res: userData.countryOfResidence || userData.country_res || '',
        state: userData.state || '',
        surburb: userData.suburb || userData.surburb || '',
        postcode: userData.postCode || userData.postcode || '',
        role: Array.isArray(userData.roleId) ? userData.roleId : [],
        groups: Array.isArray(userData.groupId) ? userData.groupId : (Array.isArray(userData.groups) ? userData.groups : []),
        department: userData.department !== undefined && userData.department !== null ? userData.department : '',
        password: userData.password || ''
      };
      const response = await axiosInstance.post('/CreateMembership/sign-up', payload);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      return rejectWithValue(axiosError.response?.data?.message || 'Signup failed');
    }
  }
);

// ** Join Community - For JoinCommunity.tsx **
export const joinCommunity = createAsyncThunk(
  'loginSignup/joinCommunity',
  async (
    userData: {
      fullName: string;
      email: string;
      password: string;
      phone: string;
      surburb: string;
      state: string;
      country_res: string;
      postcode: string;
      groups: string[];
      role?: string[];
    },
    { rejectWithValue }
  ) => {
    try {
      const payload = {
        ...userData,
        role: Array.isArray(userData.role) ? userData.role : []
      };
      const response = await axiosInstance.post('/CreateMembership/sign-up', payload);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string; error: string }>;
      const errorMsg =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Failed to join community';
      return rejectWithValue(errorMsg);
    }
  }
);

// ** Verify OTP After Signup **
export const verifySignupOtp = createAsyncThunk(
  'loginSignup/verifySignupOtp',
  async ({ username, otp }: { username: string; otp: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/signup-verify-otp', {
        username,
        otp
      });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string; error: string }>;
      const errorMsg =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Invalid or expired OTP';
      return rejectWithValue(errorMsg);
    }
  }
);

// ** Forgot Password - Send OTP **
export const forgotPasswordSendOtp = createAsyncThunk(
  'loginSignup/forgotPasswordSendOtp',
  async ({ email }: { email: string }, { rejectWithValue }) => {
    try {
      if (!email) {
        return rejectWithValue('Email is required');
      }
      const response = await axiosInstance.post('/ForgotPassword_SendOTP', { email });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string; error: string }>;
      const errorMsg =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Failed to send OTP';
      return rejectWithValue(errorMsg);
    }
  }
);

// ** Reset Password **
export const resetPassword = createAsyncThunk(
  'loginSignup/resetPassword',
  async (
    {
      email,
      otp,
      newPassword,
      confirmPassword
    }: { email: string; otp: string; newPassword: string; confirmPassword: string },
    { rejectWithValue }
  ) => {
    try {
      // Validate inputs
      if (!email || !otp || !newPassword || !confirmPassword) {
        return rejectWithValue('All fields are required');
      }
      if (newPassword !== confirmPassword) {
        return rejectWithValue('Passwords do not match');
      }
      if (newPassword.length < 8) {
        return rejectWithValue('Password must be at least 8 characters');
      }

      const response = await axiosInstance.post('/ResetPassword', {
        email,
        otp,
        newPassword,
        confirmPassword
      });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string; error: string }>;
      const errorMsg =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Password reset failed';
      return rejectWithValue(errorMsg);
    }
  }
);

// ** Update User Profile **
export const updateUserProfile = createAsyncThunk(
  'loginSignup/updateUserProfile',
  async (
    userData: {
      id: string;
      name: string;
      email: string;
      phone: string;
      dob: string;
      country_res: string;
      state: string;
      surburb: string;
      postcode: string;
      role?: string[];
      department?: string;
      groups?: string[];
      password?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const payload = {
        ...userData,
        role: Array.isArray(userData.role) ? userData.role : []
      };
      const response = await axiosInstance.post(
        '/MembershipApplicants/EditApplicant_Details',
        payload
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string; error: string }>;
      const errorMsg =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Failed to update profile';
      return rejectWithValue(errorMsg);
    }
  }
);

// ** Slice Definition **
const loginSignupSlice = createSlice({
  name: 'loginSignup',
  initialState,
  reducers: {
    resetError: (state) => {
      state.error = null;
    },
    setSelectedCountry: (state, action: PayloadAction<string>) => {
      state.selectedCountry = action.payload;
      const selectedCountry = state.countries.find((country) => country.name === action.payload);
      state.states = selectedCountry?.states ?? [];
      state.selectedState = '';
      state.cities = [];
    },
    setSelectedState: (state, action: PayloadAction<string>) => {
      state.selectedState = action.payload;
      state.cities = [];
    },
    // ** NEW: Set selected group **
    setSelectedGroup: (state, action: PayloadAction<Group | null>) => {
      state.selectedGroup = action.payload;
      // Auto-select first available role when a group is selected.
      if (action.payload) {
        state.selectedRole = state.roles.length > 0 ? state.roles[0] : null;
      }
    },
    // ** NEW: Set selected role **
    setSelectedRole: (state, action: PayloadAction<{ id: string; name: string } | null>) => {
      state.selectedRole = action.payload;
    },
    showToast: (
      state,
      action: PayloadAction<{ message: string; severity: 'success' | 'error' | 'info' }>
    ) => {
      state.toast = action.payload;
    },
    clearToast: (state) => {
      state.toast = null;
      state.messages = [];
    },
    // ** NEW: Reset signup state **
    resetSignupState: (state) => {
      state.selectedGroup = null;
      state.selectedRole = null;
      state.error = null;
    },
    logout: (state) => {
      // Reset login state
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.roles = [];
      state.departments = [];
      state.groups = []; // Clear groups on logout
      state.selectedGroup = null;
      state.selectedRole = null;

      // Clear all localStorage and sessionStorage
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (error) {
        console.error('Error clearing storage:', error);
      }

      // Remove axios headers
      delete axiosInstance.defaults.headers.common['Authorization'];
      delete axios.defaults.headers.common['Authorization'];
    }
  },
  extraReducers: (builder) => {
    builder
      // ** Login (Initiate OTP) **
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.toast = {
          message: action.payload.message,
          severity: 'success'
        };
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.toast = {
          message: action.payload as string,
          severity: 'error'
        };
      })
      // ** Verify OTP for Login **
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        verifyOtp.fulfilled,
        (
          state,
          action: PayloadAction<{
            user: User;
            token: string;
            groups: string[];
            avatar: string;
            userId: string;
          }>
        ) => {
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.isAuthenticated = true;
          state.loading = false;
          state.toast = {
            message: 'Login successful!',
            severity: 'success'
          };

          const flattenedDetails = {
            name: action.payload.user.name,
            email: action.payload.user.email,
            role: action.payload.user.role,
            avatar: action.payload.avatar || 'Unknown',
            groups: action.payload.groups,
            token: action.payload.token,
            userId: action.payload.userId // Ensure userId is included
          };

          localStorage.setItem('user-details', JSON.stringify(flattenedDetails));
          axiosInstance.defaults.headers.common['Authorization'] = action.payload.token;
          axios.defaults.headers.common['Authorization'] = action.payload.token;
        }
      )
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.toast = {
          message: action.payload as string,
          severity: 'error'
        };
      })
      // ** Check User Exists **
      .addCase(checkUserExists.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkUserExists.fulfilled, (state, action) => {
        state.loading = false;
        // No toast for successful email check
      })
      .addCase(checkUserExists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.toast = {
          message: action.payload as string,
          severity: 'error'
        };
      })
      // ** Fetch Roles & Departments **
      .addCase(fetchRolesAndDepartments.fulfilled, (state, action) => {
        state.roles = action.payload.roles;
        state.departments = action.payload.departments;
      })
      .addCase(fetchRolesAndDepartments.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch roles and departments';
      })
      // ** Fetch Countries **
      .addCase(fetchCountries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.countries = action.payload;
        state.loading = false;
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch countries';
        state.countries = [];
      })
      // ** Fetch Cities **
      .addCase(fetchCities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCities.fulfilled, (state, action) => {
        state.cities = action.payload;
        state.loading = false;
      })
      .addCase(fetchCities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch cities';
        state.cities = [];
      })
      // ** Fetch Groups List - NEW **
      .addCase(fetchGroupsList.pending, (state) => {
        state.groupsLoading = true; 
        state.error = null;
      })
      .addCase(fetchGroupsList.fulfilled, (state, action: PayloadAction<Group[]>) => {
        state.groupsLoading = false; 
        state.groups = action.payload;
      })
      .addCase(fetchGroupsList.rejected, (state, action) => {
        state.groupsLoading = false;  
        state.error = action.payload as string;
        state.toast = {
          message: action.payload as string,
          severity: 'error'
        };
      })
      // ** Signup User **
      .addCase(signupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        signupUser.fulfilled,
        (state, action: PayloadAction<{ user: User; token: string; groups: string[] }>) => {
          state.loading = false;
          state.toast = {
            message: 'Signup successful!',
            severity: 'success'
          };
          // Reset signup state after successful signup
          state.selectedGroup = null;
          state.selectedRole = null;
        }
      )
      .addCase(signupUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.toast = {
          message: action.payload as string,
          severity: 'error'
        };
      })
      // ** Join Community **
      .addCase(joinCommunity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(joinCommunity.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.toast = {
          message: 'Successfully joined community!',
          severity: 'success'
        };
        // Reset signup state after successful join
        state.selectedGroup = null;
        state.selectedRole = null;
      })
      .addCase(joinCommunity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.toast = {
          message: action.payload as string,
          severity: 'error'
        };
      })
      // ** Verify Signup OTP **
      .addCase(verifySignupOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifySignupOtp.fulfilled, (state, action) => {
        state.loading = false;
        let msg = 'Email verified! Awaiting admin approval.';
        if (
          action.payload &&
          typeof action.payload === 'object' &&
          action.payload.message &&
          action.payload.message.toLowerCase().includes('verification is pending')
        ) {
          msg = 'Your verification is pending. Please verify your email first.';
        }
        state.toast = {
          message: msg,
          severity: 'info'
        };
      })
      .addCase(verifySignupOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.toast = {
          message: action.payload as string,
          severity: 'error'
        };
      })
      // ** Forgot Password - Send OTP **
      .addCase(forgotPasswordSendOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPasswordSendOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.toast = {
          message: 'OTP sent to your email',
          severity: 'success'
        };
      })
      .addCase(forgotPasswordSendOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.toast = {
          message: action.payload as string,
          severity: 'error'
        };
      })
      // ** Reset Password **
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.toast = {
          message: 'Password reset successful! Please login with your new password.',
          severity: 'success'
        };
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.toast = {
          message: action.payload as string,
          severity: 'error'
        };
      })
      // ** Update User Profile **
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        const passwordChanged = 'password' in action.meta.arg && action.meta.arg.password;
        state.toast = {
          message: passwordChanged
            ? 'Profile and password updated successfully'
            : 'Profile updated successfully',
          severity: 'success'
        };
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.toast = {
          message: action.payload as string,
          severity: 'error'
        };
      });
  }
});

// ** Export Actions & Reducer **
export const {
  resetError,
  setSelectedCountry,
  setSelectedState,
  setSelectedGroup,
  setSelectedRole,
  resetSignupState,
  showToast,
  clearToast,
  logout
} = loginSignupSlice.actions;
export default loginSignupSlice.reducer;
